import React, { useState, useRef, useEffect, useCallback, memo } from 'react';
import { createPortal } from 'react-dom';
import type { FilterMultiSelectProps } from '../FilterPanel.types';

const ChevronDownIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M4 6l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CheckIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M2 6l3 3 5-6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

function FilterMultiSelectComponent({ config, value, onChange, compact }: FilterMultiSelectProps): React.ReactElement {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedCount = value.length;
  const selectedLabels = config.options
    ?.filter(opt => value.includes(opt.value))
    .map(opt => opt.label)
    .join(', ');

  const calculatePosition = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    setPosition({
      top: rect.bottom + 4,
      left: rect.left,
      width: Math.max(rect.width, 200)
    });
  }, []);

  useEffect(() => {
    if (isOpen) {
      calculatePosition();
      window.addEventListener('resize', calculatePosition);
      window.addEventListener('scroll', () => setIsOpen(false), true);
      return () => {
        window.removeEventListener('resize', calculatePosition);
        window.removeEventListener('scroll', () => setIsOpen(false), true);
      };
    }
  }, [isOpen, calculatePosition]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleToggle = (optionValue: string) => {
    if (value.includes(optionValue)) {
      onChange(value.filter(v => v !== optionValue));
    } else {
      onChange([...value, optionValue]);
    }
  };

  const handleSelectAll = () => {
    if (config.options) {
      onChange(config.options.map(opt => opt.value));
    }
  };

  const handleClearAll = () => {
    onChange([]);
  };

  const dropdown = isOpen && config.options && (
    <div
      ref={dropdownRef}
      style={{
        position: 'fixed',
        top: position.top,
        left: position.left,
        width: position.width,
        maxHeight: '280px',
        background: 'var(--color-bg-elevated)',
        border: '1px solid var(--color-border-default)',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--shadow-lg)',
        zIndex: 9999,
        animation: 'scaleIn var(--transition-fast) forwards',
        overflow: 'hidden'
      }}
    >
      {/* Header with select all / clear all */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        padding: 'var(--space-2) var(--space-3)',
        borderBottom: '1px solid var(--color-border-subtle)',
        background: 'var(--color-bg-secondary)'
      }}>
        <button
          onClick={handleSelectAll}
          style={{
            fontSize: 'var(--text-xs)',
            fontWeight: 500,
            color: 'var(--color-primary-600)',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 0
          }}
        >
          Tümünü Seç
        </button>
        <button
          onClick={handleClearAll}
          style={{
            fontSize: 'var(--text-xs)',
            fontWeight: 500,
            color: 'var(--color-text-secondary)',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 0
          }}
        >
          Temizle
        </button>
      </div>

      {/* Options */}
      <div style={{ maxHeight: '220px', overflowY: 'auto' }}>
        {config.options.map(option => {
          const isSelected = value.includes(option.value);
          return (
            <button
              key={option.value}
              onClick={() => handleToggle(option.value)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-3)',
                width: '100%',
                padding: compact ? 'var(--space-2) var(--space-3)' : 'var(--space-3) var(--space-4)',
                fontSize: 'var(--text-sm)',
                textAlign: 'left',
                border: 'none',
                background: 'transparent',
                color: 'var(--color-text-primary)',
                cursor: 'pointer',
                transition: 'background var(--transition-fast)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'var(--color-hover-overlay)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
              }}
            >
              <span style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '16px',
                height: '16px',
                borderRadius: 'var(--radius-sm)',
                border: isSelected ? '2px solid var(--color-primary-500)' : '2px solid var(--color-neutral-300)',
                background: isSelected ? 'var(--color-primary-500)' : 'transparent',
                color: 'white',
                flexShrink: 0,
                transition: 'all var(--transition-fast)'
              }}>
                {isSelected && <CheckIcon />}
              </span>
              <span style={{ flex: 1 }}>{option.label}</span>
              {option.count !== undefined && (
                <span style={{
                  fontSize: 'var(--text-xs)',
                  color: 'var(--color-text-tertiary)',
                  padding: '0 var(--space-1)',
                  background: 'var(--color-neutral-100)',
                  borderRadius: 'var(--radius-full)'
                }}>
                  {option.count}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div>
      <label style={{
        display: 'block',
        fontSize: 'var(--text-xs)',
        fontWeight: 500,
        color: 'var(--color-text-secondary)',
        marginBottom: 'var(--space-1)'
      }}>
        {config.label}
      </label>
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          padding: compact ? 'var(--space-2) var(--space-3)' : 'var(--space-2) var(--space-3)',
          fontSize: 'var(--text-sm)',
          fontFamily: 'var(--font-body)',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--color-border-default)',
          background: 'var(--color-bg-elevated)',
          color: selectedCount > 0 ? 'var(--color-text-primary)' : 'var(--color-text-tertiary)',
          cursor: 'pointer',
          transition: 'all var(--transition-fast)'
        }}
      >
        <span style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          flex: 1,
          textAlign: 'left'
        }}>
          {selectedCount > 0
            ? (selectedCount === 1 ? selectedLabels : `${selectedCount} seçili`)
            : (config.placeholder || 'Seçiniz...')
          }
        </span>
        {selectedCount > 0 && (
          <span style={{
            fontSize: 'var(--text-xs)',
            fontWeight: 600,
            padding: '0 var(--space-1)',
            background: 'var(--color-primary-100)',
            color: 'var(--color-primary-700)',
            borderRadius: 'var(--radius-full)',
            marginRight: 'var(--space-2)'
          }}>
            {selectedCount}
          </span>
        )}
        <span style={{
          transform: isOpen ? 'rotate(180deg)' : 'rotate(0)',
          transition: 'transform var(--transition-fast)',
          flexShrink: 0
        }}>
          <ChevronDownIcon />
        </span>
      </button>
      {dropdown && createPortal(dropdown, document.body)}
    </div>
  );
}

export const FilterMultiSelect = memo(FilterMultiSelectComponent);
